/* @jsx h */
import { defineComponent, h, ref, onMounted, computed } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useMenu } from "@app/vue/providers/index";
import {
  fetchReservationRequest,
  cancelReservationRequest,
  cancelReservation,
  fetchCancelCheck,
  type CancelCheckResult,
} from "@app/server/client";
import { ReservationRequest } from "@lemmonhq/types";
import {
  formatDateInTimezone,
  formatPhone,
  formatTimeInTimezone,
} from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { getHideReservationDurationFromGuests } from "../utils";

export const ReservationRequestStatusPage = defineComponent({
  name: "ReservationRequestStatusPage",
  setup() {
    const { t } = useLocalization();
    const route = useRoute();
    const { shop } = useMenu();
    const reservationRequest = ref<
      (ReservationRequest & { reservation?: any }) | null
    >(null);
    const isLoading = ref(true);
    const error = ref<string | null>(null);
    const isCancelling = ref(false);
    const showCancelConfirm = ref(false);
    const cancelCheck = ref<CancelCheckResult | null>(null);

    const statusConfig = computed(() => {
      const status = reservationRequest.value?.status;
      switch (status) {
        case "pending":
          return {
            icon: "schedule",
            label: t("customer.reservation_request.status_pending"),
            color: "text-yellow-600",
            bgColor: "bg-yellow-50",
            borderColor: "border-yellow-200",
            description: t("customer.reservation_request.status_pending_copy"),
          };
        case "confirmed":
          return {
            icon: "check_circle",
            label: t("customer.reservation_request.status_confirmed"),
            color: "text-green-600",
            bgColor: "bg-green-50",
            borderColor: "border-green-200",
            description: t(
              "customer.reservation_request.status_confirmed_copy",
            ),
          };
        case "cancelled_by_host":
          return {
            icon: "cancel",
            label: t("customer.reservation_request.status_cancelled"),
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            description: t(
              "customer.reservation_request.status_cancelled_copy",
            ),
          };
        case "cancelled_by_user":
          return {
            icon: "cancel",
            label: t("customer.reservation_request.status_cancelled"),
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            description: t(
              "customer.reservation_request.status_cancelled_copy",
            ),
          };
        case "rejected":
          return {
            icon: "cancel",
            label: t("customer.reservation_request.status_rejected"),
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            description: t("customer.reservation_request.status_rejected_copy"),
          };
        default:
          return {
            icon: "info",
            label: status || t("customer.reservation_request.status_unknown"),
            color: "text-gray-600",
            bgColor: "bg-gray-50",
            borderColor: "border-gray-200",
            description: t("customer.reservation_request.status_information"),
          };
      }
    });

    // Use reservation timing if available, otherwise use request timing
    const displayTime = computed(() => {
      if (reservationRequest.value?.reservation) {
        return reservationRequest.value.reservation.time;
      }
      return reservationRequest.value?.requested_time;
    });

    const displayDuration = computed(() => {
      if (reservationRequest.value?.reservation) {
        return reservationRequest.value.reservation.duration_mins;
      }
      return reservationRequest.value?.duration_mins;
    });

    const hideDuration = computed(
      () =>
        reservationRequest.value?.reservation?.hide_duration_to_guests ??
        getHideReservationDurationFromGuests(shop.value?.settings),
    );

    const formattedDate = computed(() => {
      if (!displayTime.value) return "";
      // Get the restaurant's timezone (default to UTC if not set)
      const restaurantTimezone = shop.value?.timezone || "UTC";
      return formatDateInTimezone(displayTime.value, restaurantTimezone);
    });

    const formattedTime = computed(() => {
      if (!displayTime.value) return "";
      // Get the restaurant's timezone (default to UTC if not set)
      const restaurantTimezone = shop.value?.timezone || "UTC";
      return formatTimeInTimezone(displayTime.value, restaurantTimezone);
    });

    const canCancel = computed(() => {
      const status = reservationRequest.value?.status;
      return status === "pending" || status === "confirmed";
    });

    const changeReservationHref = computed(() => {
      const shopSlug = route.params.shopslug as string;
      const requestId = route.params.requestId as string;
      const search = new URLSearchParams({
        current_request_id: requestId,
      });
      return `/restaurant/${shopSlug}/reserve/tables?${search.toString()}`;
    });

    const handleCancel = async () => {
      if (!canCancel.value) return;

      try {
        isCancelling.value = true;
        const shopSlug = route.params.shopslug as string;
        const requestId = route.params.requestId as string;

        // If there's a reservation, cancel that; otherwise cancel the request
        if (reservationRequest.value?.reservation_id) {
          await cancelReservation(
            shopSlug,
            reservationRequest.value.reservation_id,
          );
        } else {
          await cancelReservationRequest(shopSlug, requestId);
        }

        // Reload the data
        const updatedRequest = await fetchReservationRequest(
          shopSlug,
          requestId,
        );
        reservationRequest.value = updatedRequest;
        showCancelConfirm.value = false;
      } catch (e) {
        error.value = t("customer.reservation_request.cancel_failed");
        console.error(e);
      } finally {
        isCancelling.value = false;
      }
    };

    onMounted(async () => {
      try {
        const requestId = route.params.requestId as string;
        const shopSlug = route.params.shopslug as string;
        reservationRequest.value = await fetchReservationRequest(
          shopSlug,
          requestId,
        );

        // Load cancel check info if reservation can be cancelled
        const status = reservationRequest.value?.status;
        if (status === "pending" || status === "confirmed") {
          try {
            cancelCheck.value = await fetchCancelCheck(shopSlug, requestId);
          } catch (e) {
            console.error("Failed to load cancel check:", e);
          }
        }
      } catch (e) {
        error.value = t("customer.reservation_request.load_failed");
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    });

    return () => (
      <div class="space-y-6">
        {isLoading.value && (
          <div
            key="loading"
            class="bg-white rounded-lg border border-gray-200 p-8 text-center"
          >
            <div class="animate-spin inline-block w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full mb-4"></div>
            <p class="text-gray-600 font-light">
              {t("customer.reservation_request.loading_details")}
            </p>
          </div>
        )}

        {error.value && (
          <div
            key="error"
            class="bg-red-50 rounded-lg border border-red-200 p-6 text-center"
          >
            <span class="material-symbols-outlined text-red-600 text-4xl mb-2 block">
              error
            </span>
            <p class="text-red-600 font-light">{error.value}</p>
          </div>
        )}

        {!isLoading.value && reservationRequest.value && (
          <div
            key="content"
            data-testid="reservation-confirmation"
            class="space-y-6"
          >
            {/* Status Card */}
            <div
              class={`rounded-lg border p-6 ${statusConfig.value.bgColor} ${statusConfig.value.borderColor}`}
            >
              <div class="flex items-center gap-3 mb-4">
                <span
                  class={`material-symbols-outlined text-3xl ${statusConfig.value.color}`}
                >
                  {statusConfig.value.icon}
                </span>
                <div>
                  <h2 class={`text-xl font-medium ${statusConfig.value.color}`}>
                    {statusConfig.value.label}
                  </h2>
                  <p class="text-gray-600 font-light text-sm">
                    {statusConfig.value.description}
                  </p>
                </div>
              </div>

              {(reservationRequest.value.response_reason ||
                reservationRequest.value.response_note) && (
                <div class="mt-4 rounded-lg border border-gray-200 bg-white p-4">
                  <p class="text-xs uppercase tracking-wide text-gray-500 font-light mb-2">
                    {t("customer.reservation_request.restaurant_message_label")}
                  </p>
                  {reservationRequest.value.response_reason && (
                    <p class="text-gray-700 font-light">
                      {t(
                        `customer.reservation_request.reason_${reservationRequest.value.response_reason}`,
                      )}
                    </p>
                  )}
                  {reservationRequest.value.response_note && (
                    <p class="text-gray-700 font-light mt-1">
                      {reservationRequest.value.response_note}
                    </p>
                  )}
                </div>
              )}
              {reservationRequest.value.status === "pending" && (
                <div
                  key="pending-quick-tip"
                  class="mt-4 rounded-lg p-4 flex items-start gap-3 bg-blue-50 border border-blue-200"
                >
                  <span class="material-symbols-outlined text-xl mt-0.5 text-blue-600">
                    lightbulb
                  </span>
                  <p class="text-sm text-blue-800 font-light">
                    {t("customer.reservation_request.pending_quick_tip")}
                    {shop.value.phone && (
                      <>
                        {" "}
                        <a
                          href={`tel:${shop.value.phone}`}
                          class="font-medium underline"
                        >
                          {formatPhone(shop.value.phone)}
                        </a>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Reservation Details */}
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <h3 class="font-serif text-xl font-light text-gray-900 mb-4">
                {t("customer.reservation_request.reservation_details")}
              </h3>
              <div class="space-y-4">
                <div class="flex items-center gap-3">
                  <span class="material-symbols-outlined text-gray-500">
                    restaurant
                  </span>
                  <div>
                    <p class="text-sm text-gray-500 font-light">
                      {t("customer.reservation_request.restaurant")}
                    </p>
                    <p class="font-light">{shop.value.name}</p>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <span class="material-symbols-outlined text-gray-500">
                    calendar_today
                  </span>
                  <div>
                    <p class="text-sm text-gray-500 font-light">
                      {t("customer.reserve.date")}
                    </p>
                    <p class="font-light">{formattedDate.value}</p>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <span class="material-symbols-outlined text-gray-500">
                    schedule
                  </span>
                  <div>
                    <p class="text-sm text-gray-500 font-light">
                      {t("customer.reserve.time")}
                    </p>
                    <p class="font-light">{formattedTime.value}</p>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <span class="material-symbols-outlined text-gray-500">
                    group
                  </span>
                  <div>
                    <p class="text-sm text-gray-500 font-light">
                      {t("customer.reserve.party_size")}
                    </p>
                    <p class="font-light">
                      {reservationRequest.value.party_size}{" "}
                      {reservationRequest.value.party_size === 1
                        ? t("customer.reserve.guest")
                        : t("customer.reserve.guests")}
                    </p>
                  </div>
                </div>

                {!hideDuration.value && displayDuration.value && (
                  <div class="flex items-center gap-3">
                    <span class="material-symbols-outlined text-gray-500">
                      hourglass_empty
                    </span>
                    <div>
                      <p class="text-sm text-gray-500 font-light">
                        {t("customer.reserve.duration")}
                      </p>
                      <p class="font-light">
                        {displayDuration.value}{" "}
                        {t("customer.reserve.minutes_short")}
                      </p>
                    </div>
                  </div>
                )}

                {reservationRequest.value.notes && (
                  <div class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-gray-500 mt-0.5">
                      notes
                    </span>
                    <div>
                      <p class="text-sm text-gray-500 font-light">
                        {t("customer.reserve.special_requests")}
                      </p>
                      <p class="font-light">{reservationRequest.value.notes}</p>
                    </div>
                  </div>
                )}

                {reservationRequest.value.requested_tags?.length ? (
                  <div class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-gray-500 mt-0.5">
                      label
                    </span>
                    <div>
                      <p class="text-sm text-gray-500 font-light">
                        {t("customer.reservation_request.requested_tags")}
                      </p>
                      <div class="flex flex-wrap gap-1 mt-1">
                        {reservationRequest.value.requested_tags.map((tag) => (
                          <span
                            key={tag.id}
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-light border border-gray-200 bg-gray-50"
                            style={
                              tag.color
                                ? { borderColor: tag.color, color: tag.color }
                                : {}
                            }
                          >
                            {tag.icon && (
                              <span
                                class="material-symbols-outlined text-xs"
                                style="font-size: 12px"
                              >
                                {tag.icon}
                              </span>
                            )}
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Change Reservation */}
            {reservationRequest.value.status === "confirmed" &&
              reservationRequest.value.request_kind !== "edit" && (
                <div class="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 class="font-serif text-xl font-light text-gray-900 mb-4">
                    {t("customer.reservation_request.need_to_modify")}
                  </h3>
                  <p class="text-gray-600 font-light mb-4">
                    {t("customer.reservation_request.modify_copy")}
                  </p>
                  <RouterLink
                    to={changeReservationHref.value}
                    class="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-light rounded-lg transition-colors"
                  >
                    <span class="material-symbols-outlined text-lg">edit</span>
                    {t("customer.reservation_request.change_reservation")}
                  </RouterLink>
                </div>
              )}

            {/* Cancel Button */}
            {canCancel.value && !showCancelConfirm.value && (
              <div
                key="cancel-section"
                class="bg-white rounded-lg border border-gray-200 p-6"
              >
                <h3 class="font-serif text-xl font-light text-gray-900 mb-4">
                  {t("customer.reservation_request.need_to_cancel")}
                </h3>
                <p class="text-gray-600 font-light mb-4">
                  {t(
                    reservationRequest.value.reservation_id
                      ? "customer.reservation_request.cancel_copy_reservation"
                      : "customer.reservation_request.cancel_copy_request",
                  )}
                </p>

                {/* Cancellation deadline info */}
                {cancelCheck.value?.has_deposit && (
                  <div
                    class={`rounded-lg p-4 mb-4 flex items-start gap-3 ${
                      cancelCheck.value.is_after_deadline
                        ? "bg-red-50 border border-red-200"
                        : "bg-blue-50 border border-blue-200"
                    }`}
                  >
                    <span
                      class={`material-symbols-outlined text-xl mt-0.5 ${
                        cancelCheck.value.is_after_deadline
                          ? "text-red-600"
                          : "text-blue-600"
                      }`}
                    >
                      {cancelCheck.value.is_after_deadline ? "warning" : "info"}
                    </span>
                    <div>
                      {cancelCheck.value.is_after_deadline ? (
                        <p class="text-sm text-red-800 font-light">
                          {t(
                            "customer.reservation_request.deadline_passed_prefix",
                          )}{" "}
                          <span class="font-medium">
                            {shop.value?.currency || "CHF"}{" "}
                            {cancelCheck.value.deposit_amount.toFixed(2)}
                          </span>{" "}
                          {t(
                            "customer.reservation_request.deadline_passed_suffix",
                          )}
                        </p>
                      ) : (
                        <p class="text-sm text-blue-800 font-light">
                          {t(
                            "customer.reservation_request.free_cancellation_until",
                          )}{" "}
                          <span class="font-medium">
                            {formatDateInTimezone(
                              cancelCheck.value.deadline_time,
                              shop.value?.timezone || "UTC",
                            )}{" "}
                            {t("customer.reserve.at")}{" "}
                            {formatTimeInTimezone(
                              cancelCheck.value.deadline_time,
                              shop.value?.timezone || "UTC",
                            )}
                          </span>
                          .{" "}
                          {t(
                            "customer.reservation_request.after_deadline_prefix",
                          )}{" "}
                          <span class="font-medium">
                            {shop.value?.currency || "CHF"}{" "}
                            {cancelCheck.value.deposit_amount.toFixed(2)}
                          </span>{" "}
                          {t(
                            "customer.reservation_request.after_deadline_suffix",
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => (showCancelConfirm.value = true)}
                  class="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-light rounded-lg transition-colors"
                >
                  <span class="material-symbols-outlined text-lg">cancel</span>
                  {t(
                    reservationRequest.value.reservation_id
                      ? "customer.reservation_request.cancel_reservation"
                      : "customer.reservation_request.cancel_request",
                  )}
                </button>
              </div>
            )}

            {/* Cancel Confirmation */}
            {showCancelConfirm.value && (
              <div
                key="cancel-confirm"
                class="bg-red-50 rounded-lg border-2 border-red-200 p-6"
              >
                <h3 class="font-serif text-xl font-medium text-red-900 mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-2xl">
                    warning
                  </span>
                  {t("customer.reservation_request.confirm_cancellation")}
                </h3>
                <p class="text-red-800 font-light mb-6">
                  {t(
                    reservationRequest.value.reservation_id
                      ? "customer.reservation_request.confirm_cancel_reservation_copy"
                      : "customer.reservation_request.confirm_cancel_request_copy",
                  )}
                </p>
                <div class="flex gap-3">
                  <button
                    onClick={() => (showCancelConfirm.value = false)}
                    disabled={isCancelling.value}
                    class="flex-1 px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 font-light rounded-lg border border-gray-300 transition-colors"
                  >
                    {t(
                      reservationRequest.value.reservation_id
                        ? "customer.reservation_request.keep_reservation"
                        : "customer.reservation_request.keep_request",
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isCancelling.value}
                    class="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-light rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {isCancelling.value ? (
                      <span
                        key="cancelling"
                        class="inline-flex items-center gap-2"
                      >
                        <div class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>
                          {t("customer.reservation_request.cancelling")}
                        </span>
                      </span>
                    ) : (
                      <span
                        key="cancel-action"
                        class="inline-flex items-center gap-2"
                      >
                        <span class="material-symbols-outlined text-lg">
                          cancel
                        </span>
                        <span>
                          {t("customer.reservation_request.yes_cancel")}
                        </span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div class="bg-white rounded-lg border border-gray-200 p-6">
              <h3 class="font-serif text-xl font-light text-gray-900 mb-4">
                {t("customer.reservation_request.need_help")}
              </h3>
              <p class="text-gray-600 font-light mb-4">
                {t("customer.reservation_request.contact_directly")}
              </p>
              {shop.value.phone && (
                <a
                  href={`tel:${shop.value.phone}`}
                  class="inline-flex items-center gap-2 text-gray-900 hover:text-gray-700 font-light"
                >
                  <span class="material-symbols-outlined text-lg">phone</span>
                  {formatPhone(shop.value.phone)}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
});
